import React, { useEffect, useRef, useState } from 'react';
import {
  List,
  ListItem,
  ListItemButton,
  ListItemContent,
  Typography,
  Sheet,
} from '@mui/joy';
import type { User } from '../../types';
import { formatUserName } from '../../hooks/useUsers';

interface MentionDropdownProps {
  users: User[];
  searchTerm: string;
  onSelect: (user: User) => void;
  onClose: () => void;
  position: { top: number; left: number };
}

export const MentionDropdown: React.FC<MentionDropdownProps> = ({
  users,
  searchTerm,
  onSelect,
  onClose,
  position,
}) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Filter users based on search term
  const filteredUsers = users.filter(user => {
    const searchLower = searchTerm.toLowerCase();
    const fullName = formatUserName(user).toLowerCase();
    const username = user.username.toLowerCase();
    return fullName.includes(searchLower) || username.includes(searchLower);
  });

  // Reset selected index when filtered users change
  useEffect(() => {
    setSelectedIndex(0);
  }, [searchTerm]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex((prev) => 
            prev < filteredUsers.length - 1 ? prev + 1 : prev
          );
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex((prev) => (prev > 0 ? prev - 1 : prev));
          break;
        case 'Enter':
          e.preventDefault();
          if (filteredUsers[selectedIndex]) {
            onSelect(filteredUsers[selectedIndex]);
          }
          break;
        case 'Escape':
          e.preventDefault();
          onClose();
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [filteredUsers, selectedIndex, onSelect, onClose]);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        onClose();
      }
    };

    setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 0);

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  if (filteredUsers.length === 0) {
    return null;
  }

  return (
    <Sheet
      ref={dropdownRef}
      variant="outlined"
      sx={{
        position: 'absolute',
        top: position.top,
        left: position.left,
        zIndex: 10000,
        maxHeight: 200,
        minWidth: 200,
        maxWidth: 300,
        overflowY: 'auto',
        borderRadius: 'sm',
        boxShadow: 'lg',
        bgcolor: 'background.surface',
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      <List size="sm" sx={{ p: 0 }}>
        {filteredUsers.map((user, index) => (
          <ListItem key={user.id} sx={{ p: 0 }}>
            <ListItemButton
              selected={index === selectedIndex}
              onClick={() => onSelect(user)}
              sx={{
                py: 0.75,
                px: 1,
                '&:hover': {
                  bgcolor: 'background.level1',
                },
              }}
            >
              <ListItemContent>
                <Typography level="body-sm" fontWeight="md">
                  {formatUserName(user)}
                </Typography>
                <Typography level="body-xs" color="neutral">
                  @{user.username}
                </Typography>
              </ListItemContent>
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Sheet>
  );
};